import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import {
  ChartConfig,
  configureXAxisRendererTicks,
  configureYAxisRendererTicks,
  createChartLegend,
  createTooltip,
  customizeAxisLabels,
  DowntimeEntry,
  prepareChartContainer,
  renderDowntimeAndHighlightEvents,
  setupCursor,
  setupCursorEvents,
} from './chartHelpers';
import { ChartViewOptions } from '../components/slices/AssetTrendDataSlice';
import { generateLineType, generateMarkers } from './chartUtils';

interface DataSet {
  title: string;
  shortUnitOfMeasure: string;
  dataPoints: { date: number; value: number }[];
}

export const createMultiYAxisChart = (
  container: HTMLElement,
  chartData: DataSet[],
  parameterData: ChartConfig[] = [],
  chartViewOptions: ChartViewOptions,
  downtimeData?: DowntimeEntry[],
  pumpCheckerEventsSlice?: any[],
  startDate?: Date,
  endDate?: Date,
) => {
  if (!prepareChartContainer(container)) {
    return;
  }

  const root = am5.Root.new(container);
  root.setThemes([am5themes_Dark.new(root)]);
  const { chartType, showTooltip } = chartViewOptions;

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      layout: root.verticalLayout,
    }),
  );

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0.2,
      baseInterval: { timeUnit: 'second', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
        minGridDistance: 100,
        minorGridEnabled: true,
        pan: 'zoom',
      }),
    }),
  );

  if (startDate && endDate) {
    xAxis.setAll({
      min: new Date(startDate).getTime(),
      max: new Date(endDate).getTime(),
      strictMinMax: true,
    });
  }

  const xAxisRenderer = xAxis.get('renderer') as am5xy.AxisRendererX;
  configureXAxisRendererTicks(xAxisRenderer);

  const axisRules = {
    Y1: (t: string) => {
      const lower = t.toLowerCase();
      return (
        lower.includes('pressure') && !(lower.includes('tubing head pressure') || lower.includes('tubing pressure'))
      );
    },

    Y2: (t: string) => t.toLowerCase().includes('tubing head pressure') || t.toLowerCase().includes('tubing pressure'),
    Y3: (t: string) => t.toLowerCase().includes('frequency'),
    Y4: (t: string) =>
      t.toLowerCase().includes('amp') || t.toLowerCase().includes('volt') || t.toLowerCase().includes('current'),
    Y5: (t: string) => t.toLowerCase().includes('temp'),
  };

  const axisPositions = {
    Y1: { opposite: false, index: 0 },
    Y2: { opposite: true, index: 1 },
    Y3: { opposite: true, index: 2 },
    Y4: { opposite: true, index: 3 },
    Y5: { opposite: true, index: 4 },
  };

  const axisMap: Record<string, am5xy.ValueAxis<am5xy.AxisRendererY>> = {};

  const seriesAxisMap: Record<string, string> = {};

  const axisTitles: Record<string, Set<string>> = {
    Y1: new Set(),
    Y2: new Set(),
    Y3: new Set(),
    Y4: new Set(),
    Y5: new Set(),
  };

  chartData.forEach((d) => {
    if (!Array.isArray(d.dataPoints) || d.dataPoints.length === 0) return;

    for (const key of Object.keys(axisRules) as (keyof typeof axisRules)[]) {
      if (axisRules[key](d.title)) {
        seriesAxisMap[d.title] = key;
        axisTitles[key].add(`${d.title} (${d.shortUnitOfMeasure})`);
        break;
      }
    }
  });

  chartData.forEach((d) => {
    const axisKey = seriesAxisMap[d.title];
    if (axisKey && d.dataPoints && d.dataPoints.length > 0) {
      axisTitles[axisKey].add(`${d.title} (${d.shortUnitOfMeasure})`);
    }
  });
  for (const key of Object.keys(axisTitles) as (keyof typeof axisPositions)[]) {
    const hasData = axisTitles[key].size > 0;
    if (hasData) {
      if (axisTitles[key].size > 0 && hasData) {
        const pos = axisPositions[key];

        const axisRenderer = am5xy.AxisRendererY.new(root, {
          opposite: pos.opposite,
          strokeWidth: 2,
          strokeOpacity: 1,
          marginRight: pos.opposite ? -10 : undefined,
          stroke: am5.color('#4A5463'),
        });

        const newAxis: am5xy.ValueAxis<am5xy.AxisRendererY> = am5xy.ValueAxis.new(root, {
          renderer: axisRenderer,
        });

        chart.yAxes.push(newAxis);
        axisMap[key] = newAxis;

        // Configure ticks for the Y axis
        configureYAxisRendererTicks(axisRenderer, { minPosition: 0 });
        newAxis.get('renderer').ticks.template.adapters.add('x', function () {
          // Adjust the tick position based on whether the axis is opposite
          return axisRenderer.get('opposite') ? 10 : newAxis.width() - 30;
        });

        const axisLabel = am5.Label.new(root, {
          rotation: pos.opposite ? 90 : -90,
          text: Array.from(axisTitles[key]).join(', '),
          y: am5.p50,
          centerX: am5.p50,
          textAlign: 'center',
          oversizedBehavior: 'wrap',
          maxHeight: 500,
          fontFamily: 'Mulish',
          fontSize: 12,
          fontWeight: '400',
          marginRight: undefined,
          minWidth: pos.opposite ? undefined : 20,
        });

        if (pos.opposite) {
          newAxis.children.push(axisLabel);
        } else {
          newAxis.children.unshift(axisLabel);
        }

        customizeAxisLabels(newAxis.get('renderer'), 14);
      }
    }
  }

  const allSeries: am5xy.LineSeries[] = [];
  const bullets: am5.Bullet[] = [];
  const legend = createChartLegend(chart, root);

  const createCursorBullet = (series: am5xy.LineSeries, markerColor?: string) => {
    const bullet = am5.Bullet.new(root, {
      sprite: am5.Circle.new(root, {
        radius: 6,
        fill: am5.color('#323F46'),
        stroke: am5.color(markerColor || '#60BFDA'),
        strokeWidth: 4,
        visible: false,
      }),
    });
    series.bulletsContainer.children.push(bullet.get('sprite'));
    return bullet;
  };

  chartData.forEach((dataset) => {
    let axisKey = seriesAxisMap[dataset.title];

    // Fallback handling for unmapped titles
    if (!axisKey) {
      axisKey = 'Y5';
    }

    const yAxis = axisMap[axisKey];
    if (!yAxis) {
      console.error(`YAxis "${axisKey}" not found for "${dataset.title}". Skipping series.`);
      return;
    }

    const matchedParam = parameterData.find((param) => param.name.toLowerCase() === dataset.title.toLowerCase());

    const lineColor = matchedParam?.lineColor || '#0000FF';
    const lineType = matchedParam?.lineType || 'solid';
    const lineWeight = matchedParam?.lineWeight || 2;
    const markerType = matchedParam?.markerType || 'circle';
    const markerColor = matchedParam?.markerColor || lineColor;
    const markerSize = matchedParam?.markerSize || 2;

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: dataset.title,
        xAxis,
        yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stroke: am5.color(lineColor),
      }),
    );

    generateLineType(series, lineType, lineWeight);

    if (chartType === 'scatter') {
      series.strokes.template.setAll({
        strokeOpacity: 0,
      });
      series.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: generateMarkers(markerType, markerColor, markerSize, root),
        }),
      );
    }

    const tooltip = createTooltip('', root, {
      backgroundColor: '#001023',
      backgroundOpacity: 1,
      strokeColor: '#001023',
      textColor: '#FFFFFF',
      fontSize: 14,
      verticalPosition: 'center',
      chartHeight: chart.plotContainer.height(),
    });

    if (showTooltip) {
      series.set('tooltip', tooltip);
    }

    series.data.setAll(dataset.dataPoints);
    allSeries.push(series);
    bullets.push(createCursorBullet(series, chartType === 'scatter' ? markerColor : lineColor));
    legend.data.push(series);
    series.set('legendLabelText', dataset.title);
    (series as any).setPrivate('legendMarkerFill', am5.color(chartType === 'scatter' ? markerColor : lineColor));
  });

  const cursor = setupCursor(chart, root, xAxis, '#FB743C', 3);

  setupCursorEvents(cursor, xAxis, chart, allSeries, bullets, chartData, parameterData, chartType);
  renderDowntimeAndHighlightEvents(
    root,
    xAxis,
    chartViewOptions,
    downtimeData,
    showTooltip,
    pumpCheckerEventsSlice,
    true,
  );

  return root;
};
